import { h, Host } from "@stencil/core";
import { isRTL, subscribeToRTLChanges, unsubscribeToRTLChanges } from "../../common/utils";
import { addObservable, notifySubscribers, removeObservable } from "./expanded-change-obervables";
let autoId = 0;
export class ChSidebar {
    constructor() {
        this.rtl = false;
        this.expandButtonCollapseAccessibleName = undefined;
        this.expandButtonExpandAccessibleName = undefined;
        this.expandButtonCollapseCaption = undefined;
        this.expandButtonExpandCaption = undefined;
        this.expanded = true;
        this.expandButtonPosition = "after";
        this.showExpandButton = false;
    }
    /**
     * This ID is used to identify the control. Useful when the expandedChange
     * event is fired and the subscribed nodes travel all the tree up to the root
     * searching for the ID that emitted the expandedChange event.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #sidebarId;
    el;
    expandedChanged(newValue) {
        notifySubscribers(this.#sidebarId, newValue);
    }
    /**
     * Emitted when the element is clicked or the space key is pressed and
     * released.
     */
    expandedChange;
    #handleExpandedChange = (event) => {
        event.stopPropagation();
        const newExpandedValue = !this.expanded;
        this.expanded = newExpandedValue;
        this.expandedChange.emit(newExpandedValue);
    };
    #updatePositionWithRTL = (rtl) => {
        this.rtl = rtl;
    };
    #renderExpandButton = () => {
        const accessibleName = this.expanded
            ? this.expandButtonCollapseAccessibleName
            : this.expandButtonExpandAccessibleName;
        const caption = this.expanded
            ? this.expandButtonCollapseCaption
            : this.expandButtonExpandCaption;
        return (h("button", { "aria-label": accessibleName !== caption ? accessibleName : undefined, class: {
                "expand-button": true,
                "expand-button--expanded-ltr": this.expanded && !this.rtl,
                "expand-button--collapsed": !this.expanded,
                "expand-button--collapsed-rtl": !this.expanded && this.rtl
            }, part: `expand-button ${this.expanded ? "expanded" : "collapsed"}`, type: "button", onClick: this.#handleExpandedChange }, caption));
    };
    connectedCallback() {
        this.#sidebarId ??= `ch-sidebar-${autoId++}`;
        // The ID MUST be set in this instance, because when searching for the
        // ancestors in `syncStateWithObservableAncestors` we use the DOM id and
        // the method could be executed before the first render of the sidebar,
        // where we typically attach the Host attributes, in this case, the id attr
        this.el.id = this.#sidebarId;
        addObservable(this.#sidebarId, this.expanded);
        notifySubscribers(this.#sidebarId, this.expanded); // Must run after the ID and the observable has been configured
        // Set RTL watcher
        subscribeToRTLChanges(this.#sidebarId, this.#updatePositionWithRTL);
        // Initialize RTL position
        this.#updatePositionWithRTL(isRTL());
    }
    disconnectedCallback() {
        removeObservable(this.#sidebarId);
        // Disconnect RTL watcher to avoid memory leaks
        unsubscribeToRTLChanges(this.#sidebarId);
    }
    render() {
        return (h(Host, { key: '5098ceea0f85b36d5af0c0f2b57ffd5809c4ec3e', class: this.expanded ? "ch-sidebar--expanded" : "ch-sidebar--collapsed" }, this.showExpandButton &&
            this.expandButtonPosition === "before" &&
            this.#renderExpandButton(), h("slot", { key: '2bf74092bdc6bf75470297b888b1301ef75e0f55' }), this.showExpandButton &&
            this.expandButtonPosition === "after" &&
            this.#renderExpandButton()));
    }
    static get is() { return "ch-sidebar"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["sidebar.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["sidebar.css"]
        };
    }
    static get properties() {
        return {
            "expandButtonCollapseAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor expand button when `expanded = true`."
                },
                "attribute": "expand-button-collapse-accessible-name",
                "reflect": false
            },
            "expandButtonExpandAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor expand button when `expanded = false`."
                },
                "attribute": "expand-button-expand-accessible-name",
                "reflect": false
            },
            "expandButtonCollapseCaption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the caption of the expand button when `expanded = true`."
                },
                "attribute": "expand-button-collapse-caption",
                "reflect": false
            },
            "expandButtonExpandCaption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the caption of the expand button when `expanded = false`."
                },
                "attribute": "expand-button-expand-caption",
                "reflect": false
            },
            "expanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the control is expanded or collapsed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "true"
            },
            "expandButtonPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"before\" | \"after\"",
                    "resolved": "\"after\" | \"before\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the expand button relative to the content of the\nsidebar.\n - `\"before\"`: The expand button is positioned before the content of the sidebar.\n - `\"after\"`: The expand button is positioned after the content of the sidebar."
                },
                "attribute": "expand-button-position",
                "reflect": false,
                "defaultValue": "\"after\""
            },
            "showExpandButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display a expandable button at the bottom of the control."
                },
                "attribute": "show-expand-button",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "rtl": {}
        };
    }
    static get events() {
        return [{
                "method": "expandedChange",
                "name": "expandedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the element is clicked or the space key is pressed and\nreleased."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "expanded",
                "methodName": "expandedChanged"
            }];
    }
}
